/* Copyright (c) 2017 Backpack Health LLC */

import { authorizedApiRequest, authorizedApiRequestWithFullUrl } from './ApiRequest';
import ApiErrorModel from '../models/ApiErrorModel';

const HealthEntryApi = {
  getAll: function getAllHealthEntries(profileId, status) {
    return authorizedApiRequest('get', `profiles/${profileId}/healthEntries?status=${status}`);
  },
  create: function createHealthEntry(profileId) {
    return authorizedApiRequest('post', `profiles/${profileId}/healthEntries`);
  },
  updateStatus: function updateStatus(profileId, status) {
    return authorizedApiRequest('put', `profiles/${profileId}/healthEntries/${status}`);
  },
  get: function getHealthEntry(profileId, entryId) {
    return authorizedApiRequest('get', `profiles/${profileId}/healthEntries/${entryId}`);
  },
  update: function updateHealthEntry(profileId, entryId) {
    return authorizedApiRequest('put', `profiles/${profileId}/healthEntries/${entryId}`);
  },
  del: function deleteHealthEntry(profileId, entryId) {
    return authorizedApiRequest('delete', `profiles/${profileId}/healthEntries/${entryId}`);
  },
  flagged: function flagHealthEntry(profileId, entryId) {
    return authorizedApiRequest('put', `profiles/${profileId}/healthEntries/${entryId}/flagged`);
  },
  definition: function healthEntryDefinition(url) {
    return authorizedApiRequestWithFullUrl('get', url);
  },
  createValue: function createHealthEntryValue(profileId, entryId) {
    return authorizedApiRequest('post', `profiles/${profileId}/healthEntries/${entryId}/healthEntryValues`);
  },
  getAllValues: function getHealthEntryValues(profileId, entryId) {
    return authorizedApiRequest('get', `profiles/${profileId}/healthEntries/${entryId}/healthEntryValues`);
  },
  delValue: function deleteHealthEntryValue(profileId, entryId, valueId) {
    return authorizedApiRequest('delete', `profiles/${profileId}/healthEntries/${entryId}/healthEntryValues/${valueId}`);
  },
  updateValue: function updateHealthEntryValue(profileId, entryId, valueId) {
    return authorizedApiRequest('put', `profiles/${profileId}/healthEntries/${entryId}/healthEntryValues/${valueId}`);
  },
  flaggedValue: function flagHealthEntryValue(profileId, entryId, valueId) {
    return authorizedApiRequest('put', `profiles/${profileId}/healthEntries/${entryId}/healthEntryValues/${valueId}/flagged`);
  },
};

const HealthEntryService = {
  allActive(profileId, callback) {
    return HealthEntryApi.getAll(profileId, 'active')
    .end((err, res) => {
      const apiError = err ? new ApiErrorModel(err) : null;
      callback(apiError, res.body);
    });
  },

  allPending(profileId, callback) {
    return HealthEntryApi.getAll(profileId, 'pending')
    .end((err, res) => {
      const apiError = err ? new ApiErrorModel(err) : null;
      callback(apiError, res.body);
    });
  },

  acceptPending(profileId, data) {
    return HealthEntryApi.updateStatus(profileId, 'accept')
    .send(data);
    // .end((err) => {
    //   const apiError = err ? new ApiErrorModel(err) : null;

    //   callback(apiError);
    // });
  },

  rejectPending(profileId, data) {
    return HealthEntryApi.updateStatus(profileId, 'reject')
    .send(data);
    // .end((err) => {
    //   const apiError = err ? new ApiErrorModel(err) : null;

    //   callback(apiError);
    // });
  },

  reviewPending(profileId, data) {
    const { health_entry_ids } = data;
    return authorizedApiRequest('put', `profiles/${profileId}/healthEntries/review`)
    .send({ health_entry_ids });
    // .then()
    // .end((err) => {
    //   const apiError = err ? new ApiErrorModel(err) : null;

    //   callback(apiError);
    // });
  },

  create(profileId, healthEntryData, callback) {
    return HealthEntryApi.create(profileId)
    .send(healthEntryData)
    .end((err, res) => {
      const apiError = err ? new ApiErrorModel(err) : null;
      const healthEntry = apiError ? {} : res.body.data;
      callback(apiError, healthEntry);
    });
  },

  get(profileId, entryId, callback) {
    return HealthEntryApi.get(profileId, entryId)
    .end((err, res) => {
      const apiError = err ? new ApiErrorModel(err) : null;
      const healthEntry = apiError ? {} : res.body.data;
      callback(apiError, healthEntry);
    });
  },

  update(profileId, healthEntryData, callback) {
    return HealthEntryApi.update(profileId, healthEntryData.id)
    .send(healthEntryData)
    .end((err) => {
      const apiError = err ? new ApiErrorModel(err) : null;
      callback(apiError, null);
    });
  },

  del(profileId, entryId, callback) {
    return HealthEntryApi.del(profileId, entryId)
    .end((err) => {
      const apiError = err ? new ApiErrorModel(err) : null;
      callback(apiError, null);
    });
  },

  flagged(profileId, entryId, isFlagged, callback) {
    return HealthEntryApi.flagged(profileId, entryId)
    .send({
      flagged: isFlagged,
    })
    .end((err) => {
      const apiError = err ? new ApiErrorModel(err) : null;
      callback(apiError, null);
    });
  },

  definition(url, callback) {
    return HealthEntryApi.definition(url)
    .end((err, res) => {
      const apiError = err ? new ApiErrorModel(err) : null;
      const definitionHtml = apiError ? '' : res.xhr.response;
      callback(apiError, definitionHtml);
    });
  },

  createValue(profileId, entryId, valueData, callback) {
    return HealthEntryApi.createValue(profileId, entryId)
    .send(valueData)
    .end((err, res) => {
      const apiError = err ? new ApiErrorModel(err) : null;
      const healthEntryValue = apiError ? {} : res.body.data;
      callback(apiError, healthEntryValue);
    });
  },

  getAllValues(profileId, entryId, callback) {
    return HealthEntryApi.getAllValues(profileId, entryId)
    .end((err, res) => {
      if (err) { return callback(new ApiErrorModel(err), {}); }
      return callback(null, res.body.data);
    });
  },

  delValue(profileId, entryId, valueId, callback) {
    return HealthEntryApi.delValue(profileId, entryId, valueId)
    .end((err) => {
      const apiError = err ? new ApiErrorModel(err) : null;
      callback(apiError, null);
    });
  },

  updateValue(profileId, entryId, healthEntryValueData, callback) {
    return HealthEntryApi.updateValue(profileId, entryId, healthEntryValueData.id)
    .send(healthEntryValueData)
    .end((err) => {
      const apiError = err ? new ApiErrorModel(err) : null;
      callback(apiError, null);
    });
  },

  flaggedValue(profileId, entryId, valueId, isFlagged, callback) {
    return HealthEntryApi.flaggedValue(profileId, entryId, valueId)
    .send({
      flagged: isFlagged,
    })
    .end((err) => {
      const apiError = err ? new ApiErrorModel(err) : null;
      callback(apiError, null);
    });
  },

};

export default HealthEntryService;
